/** The badge row that identifies a question wherever it appears. */
import { Badge } from "@/components/ui";
import type { BadgeTone } from "@/components/ui";
import type { QuestionStatus, QuestionWithContext } from "@exam/shared";
import {
  DIFFICULTY_LABEL,
  SOURCE_LABEL,
  STATUS_LABEL,
  TYPE_LABEL,
  labelFor,
} from "../lib/enumLabels";

const STATUS_TONE: Record<QuestionStatus, BadgeTone> = {
  pending: "review",
  approved: "answered",
  rejected: "violation",
};

/**
 * Reasons the API will refuse to put this question on a paper. Both come from
 * how upstream content behaves: NAT keys are published truncated rather than
 * rounded, so an imported key is not trustworthy until someone confirms it, and
 * plenty of questions reference figures whose images never resolved.
 */
export function blockersFor(question: QuestionWithContext): string[] {
  const out: string[] = [];
  if (question.type === "NAT" && !question.key_verified) out.push("Key unverified");
  if (question.requires_figure && question.figure_r2_key === null) {
    out.push("Figure missing");
  }
  return out;
}

export function QuestionMeta({
  question,
  showStatus = true,
}: {
  question: QuestionWithContext;
  showStatus?: boolean;
}) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <Badge>{labelFor(TYPE_LABEL, question.type)}</Badge>
      <Badge>{labelFor(DIFFICULTY_LABEL, question.difficulty)}</Badge>
      <Badge>{labelFor(SOURCE_LABEL, question.source)}</Badge>
      {showStatus ? (
        <Badge tone={STATUS_TONE[question.status]}>
          {labelFor(STATUS_LABEL, question.status)}
        </Badge>
      ) : null}
      {blockersFor(question).map((blocker) => (
        <Badge key={blocker} tone="violation">
          {blocker}
        </Badge>
      ))}
      <span className="text-2xs text-ink-faint">
        {question.subject_name} · {question.topic_name}
        {question.week_no !== null ? ` · week ${question.week_no}` : ""}
      </span>
    </div>
  );
}
